import React from 'react'
import styled from 'styled-components'
import Modal from 'core-system/Modal'
import Text from 'core-system/Text'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import palette from 'core-system/Themes/palette'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import FlashOnIcon from '@mui/icons-material/FlashOn'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import InsightsIcon from '@mui/icons-material/Insights'

const ModalContent = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`

const IconCircle = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background-color: ${palette.primary.pink100};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
`

const Title = styled(Text)`
  margin-bottom: 0.75rem;
`

const Subtitle = styled(Text)`
  color: ${palette.text.secondary};
  margin-bottom: 1.5rem;
  max-width: 320px;
`

const FeaturesList = styled.div`
  width: 100%;
  margin-bottom: 1.5rem;
`

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
  text-align: left;
`

const FeatureIconCircle = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  min-width: 2.5rem;
  border-radius: 50%;
  background-color: ${palette.grey.grey100};
  display: flex;
  align-items: center;
  justify-content: center;
`

const FeatureContent = styled.div`
  display: flex;
  flex-direction: column;
`

const FeatureTitle = styled(Text)`
  font-weight: 600;
`

const FeatureDescription = styled(Text)`
  color: ${palette.text.secondary};
`

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const SecondaryLink = styled.button`
  background: none;
  border: none;
  color: ${palette.text.secondary};
  cursor: pointer;
  font-size: 0.875rem;
  padding: 0.5rem;

  &:hover {
    color: ${palette.text.primary};
  }
`

interface FleetCardModalProps {
  open: boolean
  onClose: () => void
  onGetCard: () => void
  onContinue: () => void
}

const FleetCardModal = ({
  open,
  onClose,
  onGetCard,
  onContinue,
}: FleetCardModalProps) => {
  return (
    <Modal open={open} onClose={onClose} width='400px'>
      <ModalContent>
        <IconCircle>
          <CreditCardIcon
            sx={{ fontSize: 32, color: palette.primary.pink800 }}
          />
        </IconCircle>

        <Title variant='h4'>Use Your Fleet Card Instead</Title>

        <Subtitle variant='body2'>
          Skip the paperwork! Your Fleet card handles transactions instantly
          with automatic tracking and faster processing.
        </Subtitle>

        <FeaturesList>
          <FeatureItem>
            <FeatureIconCircle>
              <FlashOnIcon
                sx={{ fontSize: 20, color: palette.primary.pink800 }}
              />
            </FeatureIconCircle>
            <FeatureContent>
              <FeatureTitle variant='body1'>Instant Transactions</FeatureTitle>
              <FeatureDescription variant='body2'>
                No waiting for reimbursement approval
              </FeatureDescription>
            </FeatureContent>
          </FeatureItem>

          <FeatureItem>
            <FeatureIconCircle>
              <ReceiptLongOutlinedIcon
                sx={{ fontSize: 20, color: palette.primary.pink800 }}
              />
            </FeatureIconCircle>
            <FeatureContent>
              <FeatureTitle variant='body1'>No Paperwork</FeatureTitle>
              <FeatureDescription variant='body2'>
                Receipts are captured automatically
              </FeatureDescription>
            </FeatureContent>
          </FeatureItem>

          <FeatureItem>
            <FeatureIconCircle>
              <InsightsIcon
                sx={{ fontSize: 20, color: palette.primary.pink800 }}
              />
            </FeatureIconCircle>
            <FeatureContent>
              <FeatureTitle variant='body1'>Automatic Tracking</FeatureTitle>
              <FeatureDescription variant='body2'>
                All expenses organized in one place
              </FeatureDescription>
            </FeatureContent>
          </FeatureItem>
        </FeaturesList>

        <ButtonContainer>
          <PrimaryButton onClick={onGetCard} width='100%'>
            <CreditCardIcon sx={{ fontSize: 18, marginRight: '0.5rem' }} />
            View Your Fleet Card
          </PrimaryButton>
          <SecondaryLink onClick={onContinue}>
            Continue to Reimbursement
          </SecondaryLink>
        </ButtonContainer>
      </ModalContent>
    </Modal>
  )
}

export default FleetCardModal
